import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { m as motion } from "framer-motion";
import api from "../lib/api";
import KineticLines from "../components/KineticLines";
import NumberedChapter from "../components/NumberedChapter";
import Seo from "../components/Seo";

export default function Author() {
  const [settings, setSettings] = useState(null);
  const [novels, setNovels] = useState([]);
  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
    api.get("/novels").then((r) => setNovels(r.data));
  }, []);

  return (
    <div>
      <Seo
        path="/auteur"
        title="L'auteur"
        description="Bruno Courtin, auteur français. Biographie, démarche d'écriture et bibliographie chronologique — Histoire, sciences humaines, anticipation et grandes questions contemporaines."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ProfilePage",
          mainEntity: {
            "@type": "Person",
            name: "Bruno Courtin",
            jobTitle: "Auteur",
            nationality: "France",
            description: settings?.author_bio_short || "Auteur français de romans historiques, d'aventure et d'anticipation.",
            url: "https://brunocourtin.fr/auteur",
          },
        }}
      />
      {/* Intro hero */}
      <section className="theme-ivory pt-40 pb-24 px-6 lg:px-12" data-testid="author-hero">
        <div className="max-w-[1440px] mx-auto grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-8">
            <p className="eyebrow" style={{ color: "var(--copper)" }}>L'auteur</p>
            <h1 className="font-display font-light text-6xl md:text-8xl lg:text-[9rem] leading-[0.9] mt-6 text-[var(--ink)]">
              <KineticLines lines={["Bruno Courtin"]} delay={0.1} />
            </h1>
            {settings?.author_bio_short && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.9 }}
                className="mt-14 max-w-3xl font-display text-3xl md:text-4xl leading-[1.25] text-[var(--brown)]"
              >
                {settings.author_bio_short}
              </motion.p>
            )}
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 1.1, ease: [0.19, 1, 0.22, 1] }}
            className="col-span-12 md:col-span-4 flex md:justify-end items-start mt-16 md:mt-0"
            data-testid="author-hero-portrait"
          >
            <div className="relative w-full max-w-[420px]">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/files/bruno-portrait.jpg`}
                alt="Portrait de Bruno Courtin"
                loading="eager"
                fetchPriority="high"
                className="w-full h-auto block"
                data-testid="author-portrait-img"
                style={{
                  filter: "sepia(0.35) contrast(0.96) brightness(1.05)",
                  WebkitMaskImage:
                    "radial-gradient(ellipse 68% 78% at 55% 42%, #000 55%, rgba(0,0,0,0.9) 72%, rgba(0,0,0,0.45) 88%, rgba(0,0,0,0) 100%)",
                  maskImage:
                    "radial-gradient(ellipse 68% 78% at 55% 42%, #000 55%, rgba(0,0,0,0.9) 72%, rgba(0,0,0,0.45) 88%, rgba(0,0,0,0) 100%)",
                }}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Longue biographie */}
      <section className="theme-parchment px-6 lg:px-12 py-24 md:py-36" data-testid="author-bio">
        <div className="max-w-[1440px] mx-auto grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4">
            <p className="eyebrow" style={{ color: "var(--copper)" }}>Portrait</p>
            <h2 className="font-display text-4xl md:text-5xl mt-3 text-[var(--ink)]">Une écriture d'atlas</h2>
          </div>
          <div className="col-span-12 md:col-span-8 md:pl-8">
            {settings?.author_bio_long?.split("\n\n").map((p, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.08 }}
                className="text-[17px] leading-[1.95] text-[var(--brown)] mb-6 max-w-2xl font-body"
              >
                {p}
              </motion.p>
            ))}
          </div>
        </div>
      </section>

      {/* Valeurs d'écriture */}
      <section className="theme-ink px-6 lg:px-12 py-24 md:py-36" data-testid="author-values">
        <div className="max-w-[1440px] mx-auto">
          <div className="grid grid-cols-12 gap-6 mb-16">
            <div className="col-span-12 md:col-span-4">
              <p className="eyebrow" style={{ color: "var(--gold-soft)" }}>Valeurs</p>
            </div>
            <div className="col-span-12 md:col-span-8">
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight">Ce qui traverse les livres</h2>
            </div>
          </div>
          <NumberedChapter number={1} title="Documentation historique et scientifique" index={0}>
            Chaque roman s'appuie sur des sources précises — archives, cartographies, corpus scientifiques — pour tenir la vérité du monde qu'il propose.
          </NumberedChapter>
          <NumberedChapter number={2} title="Force narrative" index={1}>
            Un souffle romanesque assumé. Des intrigues qui portent, des rythmes tenus, des personnages incarnés.
          </NumberedChapter>
          <NumberedChapter number={3} title="Profondeur humaine" index={2}>
            Le pouvoir, la peur, la disparition, le choix. Ce que les grandes bascules font aux vies particulières.
          </NumberedChapter>
          <NumberedChapter number={4} title="Ouverture internationale" index={3}>
            Des récits qui traversent les océans et les langues. Le monde comme matière romanesque, pas comme décor.
          </NumberedChapter>
        </div>
      </section>

      {/* Bibliographie */}
      <section className="theme-ivory px-6 lg:px-12 py-24 md:py-36" data-testid="author-bibliography">
        <div className="max-w-[1440px] mx-auto">
          <p className="eyebrow" style={{ color: "var(--copper)" }}>Bibliographie</p>
          <h2 className="font-display text-5xl md:text-6xl mt-3 text-[var(--ink)]">Les livres, par ordre d'apparition</h2>

          <div className="mt-16">
            {novels.map((n, i) => (
              <motion.div
                key={n.id}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.05 }}
                className="grid grid-cols-12 gap-6 py-8 border-t border-[var(--line)] items-baseline"
              >
                <div className="col-span-3 md:col-span-1 font-mono text-[13px] text-[var(--copper)] tracking-widest">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="col-span-9 md:col-span-6">
                  <h3 className="font-display text-3xl md:text-4xl text-[var(--ink)] leading-tight">{n.title}</h3>
                  {n.subtitle && <p className="font-display italic text-xl text-[var(--brown)] mt-1">{n.subtitle}</p>}
                </div>
                <div className="hidden md:block col-span-3 text-[13px] font-mono uppercase tracking-widest text-[var(--brown)]/70">
                  {n.genre}
                </div>
                <div className="col-span-12 md:col-span-2 mt-2 md:mt-0">
                  <Link to={`/romans/${n.slug}`} className="text-[11px] font-mono uppercase tracking-widest text-[var(--ink)] hover:text-[var(--copper)] transition-colors">
                    Découvrir →
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PARCOURS PROFESSIONNEL ET INSPIRATIONS */}
      <section className="theme-parchment px-6 lg:px-12 py-24 md:py-36" data-testid="author-inspirations">
        <div className="max-w-[1440px] mx-auto">
          <p className="eyebrow" style={{ color: "var(--copper)" }}>Parcours professionnel et inspirations</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] mt-3 text-[var(--ink)] max-w-3xl">
            Les autres terrains d&apos;écriture
          </h2>
          <p className="mt-8 max-w-2xl text-[15.5px] leading-relaxed text-[var(--brown)]/85">
            Avant et à côté des romans, Bruno Courtin conduit plusieurs activités qui nourrissent son écriture :
            accompagnement des transitions professionnelles, ingénierie pédagogique, outils numériques et pratique
            de l&apos;aïkido. Ces terrains ne sont pas des partenariats commerciaux liés aux romans ; ils constituent
            l&apos;arrière-plan concret d&apos;une œuvre qui interroge la mémoire, la responsabilité et la
            transmission.
          </p>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
            {[
              {
                key: "istas",
                eyebrow: "Formation et accompagnement professionnel",
                title: "ISTAS",
                anchor: "Centre de formation et de bilan de compétences ISTAS",
                url: "https://istas.fr",
                pitch: "Accompagne les évolutions professionnelles, les transitions et les bilans de compétences depuis plus de vingt ans. Un terrain d\u2019écoute qui informe la construction psychologique de nombreux personnages.",
              },
              {
                key: "human-knowledge",
                eyebrow: "Ingénierie pédagogique et intelligence artificielle",
                title: "Human Knowledge",
                anchor: "Cabinet d\u2019ingénierie pédagogique Human Knowledge",
                url: "https://humanknowledge.fr",
                pitch: "Cabinet d\u2019ingénierie pédagogique explorant les usages responsables de l\u2019intelligence artificielle. Un chantier proche de l\u2019univers de NeuroAurum et des Chaînes du Vivant.",
              },
              {
                key: "nextcap",
                eyebrow: "Solutions numériques d\u2019accompagnement",
                title: "NEXTCAP",
                anchor: "Plateforme professionnelle NEXTCAP",
                url: "https://nextcap-pro.com",
                pitch: "Plateforme numérique pensée pour outiller concrètement les professionnels de l\u2019accompagnement. Une matière première pour interroger, dans les récits, la place des outils dans les décisions humaines.",
              },
              {
                key: "way-of-dojo",
                eyebrow: "Aïkido et inspiration philosophique",
                title: "Way of Dojo",
                anchor: "Projet Way of Dojo consacré à l\u2019aïkido",
                url: "https://wayofdojo.fr",
                pitch: "Projet consacré à l\u2019aïkido et à ses prolongements réflexifs. La distinction entre réaction automatique et réponse consciente, présente dans plusieurs romans, y trouve sa source pratique.",
              },
            ].map((b, i) => (
              <motion.div
                key={b.key}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                data-testid={`inspiration-${b.key}`}
                className="border-t border-[var(--line)] pt-6"
              >
                <p className="eyebrow" style={{ color: "var(--copper)" }}>{b.eyebrow}</p>
                <h3 className="font-display text-2xl md:text-3xl text-[var(--ink)] mt-2">{b.title}</h3>
                <p className="mt-4 text-[14.5px] leading-relaxed text-[var(--brown)]/85 max-w-md">{b.pitch}</p>
                {/* eslint-disable-next-line react/jsx-no-target-blank */}
                <a
                  href={b.url}
                  target="_blank"
                  rel="noopener"
                  data-testid={`inspiration-link-${b.key}`}
                  className="mt-5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-[var(--copper)] hover:gap-3 transition-all duration-300 border-b border-[var(--copper)]/40 hover:border-[var(--copper)] pb-0.5"
                >
                  {b.anchor} <span aria-hidden>↗</span>
                </a>
              </motion.div>
            ))}
          </div>

          <p className="mt-16 max-w-2xl text-[13px] italic text-[var(--brown)]/70">
            brunocourtin.fr demeure le site officiel de l&apos;auteur. Les projets mentionnés ci-dessus disposent
            chacun de leur propre site et ne sont pas liés commercialement aux romans.
          </p>
        </div>
      </section>
    </div>
  );
}
